import { Heart, Star, ThumbsUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { IFieldSettings } from '@/features/module-builder/types';

const RATING_ICONS: Record<string, LucideIcon> = {
    stars: Star,
    hearts: Heart,
    thumbs: ThumbsUp,
};

const MAX_RATING_SCALE = 10;

export interface RatingInputProps {
    /** Current rating (0 = unset). */
    value: number;
    onChange?: (value: number) => void;
    settings: IFieldSettings | null;
    disabled?: boolean;
    /** Renders a non-interactive icon row (used by RecordFieldValue). */
    readOnly?: boolean;
}

/**
 * Numeric rating control for record forms. Clicking icon `i` sets `i + 1`;
 * clicking the current value clears back to 0.
 */
export function RatingInput({ value, onChange, settings, disabled, readOnly }: RatingInputProps) {
    const { t } = useTranslation();
    const scale = Math.min(settings?.scale ?? 5, MAX_RATING_SCALE);
    const color = settings?.color ?? '#EAB308';
    const Icon = RATING_ICONS[settings?.symbol ?? 'stars'] ?? Star;

    if (readOnly) {
        return (
            <span className="inline-flex items-center gap-1">
                {Array.from({ length: scale }).map((_, index) => (
                    <Icon
                        key={index}
                        aria-hidden="true"
                        className="size-4"
                        style={{ color, fill: index < value ? color : 'transparent' }}
                    />
                ))}
            </span>
        );
    }

    return (
        <div className="flex h-9 items-center gap-1">
            {Array.from({ length: scale }).map((_, index) => (
                <button
                    key={index}
                    type="button"
                    disabled={disabled}
                    aria-label={t('recordForm.setRating', {
                        defaultValue: 'Set rating to {{value}}',
                        value: index + 1,
                    })}
                    aria-pressed={index < value}
                    onClick={() => onChange?.(value === index + 1 ? 0 : index + 1)}
                    className={cn(
                        'transition-transform',
                        disabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer hover:scale-110',
                    )}
                >
                    <Icon
                        className="size-5 transition-colors"
                        style={{ color, fill: index < value ? color : 'transparent' }}
                    />
                </button>
            ))}
        </div>
    );
}
